import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Card, Chip, CircularProgress, Dialog, DialogContent, DialogTitle, FormControlLabel, Grid, Icon, IconButton, InputLabel, LinearProgress, MenuItem,
  Switch, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip,
} from "@mui/material";
import moment from "moment";
import { Bar, Doughnut } from "react-chartjs-2";
import {
  Chart as ChartJS, ArcElement, CategoryScale, LinearScale, BarElement, LineController, LineElement, PointElement, Tooltip as ChartTooltip, Legend,
} from "chart.js";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";
import MDSelect from "components/MDSelect";
import MDPeriodo from "components/MDPeriodo";
import { useMaterialUIController } from "context";
import { get, getFile } from "utils/WebService";
import { errorAlert } from "utils/Functions";
import { coresDoTema } from "assets/coresTema";
import KpiCard from "views/coordenador/relatorios/analiseTempo/KpiCard";
import Painel from "views/coordenador/relatorios/analiseTempo/Painel";
import { formatarNumero, temaGrafico, corTextoSobre } from "views/coordenador/relatorios/analiseTempo/analiseUtils";

ChartJS.register(ArcElement, CategoryScale, LinearScale, BarElement, LineController, LineElement, PointElement, ChartTooltip, Legend);

// Cores conferidas no validador de paleta, por gráfico (nunca dividem o mesmo gráfico os dois grupos):
// - entrega: azul (entregue), vermelho (não entregue), cinza (aguardando / sem medição); não verde e vermelho, que ficam
//   iguais para daltônicos;
// - engajamento: verde (abertura) e âmbar (clique), sempre com o número na barra;
// - o funil começa no cinza-escuro neutro (enviadas) e segue com a cor de cada etapa.
// No escuro (cinza médio do sistema) os tons passam em contraste, daltonismo e distinção. Nunca só pela cor.
const CORES = {
  enviadas: { light: "#344767", dark: "#d9d9d9" },
  entregues: { light: "#2a78d6", dark: "#7fb2f0" },
  naoEntregues: { light: "#d03b3b", dark: "#f08c8c" },
  aguardando: { light: "#898781", dark: "#b5b4ab" },
  semMedicao: { light: "#c9ccd1", dark: "#8f8f8f" },
  aberturas: { light: "#13996b", dark: "#62cfa6" },
  cliques: { light: "#c98500", dark: "#f0b44a" },
  // Respostas dos devedores (ocorrência registrada pelo operador): só no card e na coluna dos lotes, sem gráfico com as outras
  respostas: { light: "#b4357a", dark: "#e07ab1" },
};
const ROTULOS = {
  entregues: "Entregues (48 h sem aviso)",
  naoEntregues: "Não entregues",
  // Sem aviso de não entrega, mas enviados há menos de 48 h (o aviso ainda pode voltar): contam como entregues
  aguardando: "Entregues há menos de 48 h",
  semMedicao: "Sem medição (lote anterior)",
};
const ORDEM = ["entregues", "naoEntregues", "aguardando", "semMedicao"];
const SITUACAO_LOTE = {
  ENVIANDO: { label: "Enviando", cor: "info" },
  PAUSADO: { label: "Pausado", cor: "warning" },
  CONCLUIDO: { label: "Concluído", cor: "success" },
  CANCELADO: { label: "Cancelado", cor: "secondary" },
};
const ENTREGA = {
  ENTREGUE: { label: "Entregue", cor: "success" },
  AGUARDANDO: { label: "Aguardando retorno", cor: "info" },
  NAO_ENTREGUE: { label: "Não entregue", cor: "error" },
  PARCIAL: { label: "Entregue em parte", cor: "warning" },
};
const SITUACAO_ENVIO = {
  PENDENTE: "Na fila", ENVIANDO: "Enviando", ENVIADO: "Enviado", ERRO: "Erro", SEM_EMAIL: "Sem e-mail válido", CANCELADO: "Cancelado",
};
const OPCOES_SITUACAO = Object.entries(SITUACAO_LOTE).map(([value, s]) => ({ value, label: s.label }));
const DIMENSOES = [
  { chave: "porCliente", campo: "cliente", rotulo: "Cliente" },
  { chave: "porCarteira", campo: "carteira", rotulo: "Carteira" },
  { chave: "porModelo", campo: "modelo", rotulo: "Modelo" },
];

const rotuloFiltro = { px: 1, pb: 0.5, pt: 0.2 };
// Os 7 cards numa linha só em tela bem larga (abaixo disso ficam 4 + 3: com o menu aberto, 7 cards em 1400 px ficariam com ~150 px)
const SETE_POR_LINHA = { "@media (min-width: 1800px)": { flexBasis: `${100 / 7}%`, maxWidth: `${100 / 7}%` } };
const FONTE = "Roboto, Helvetica, Arial, sans-serif";
const percentual = (v) => (v === null || v === undefined ? "-" : `${String(v).replace(".", ",")}%`);
const parte = (valor, total) => (total > 0 ? Math.round((valor * 1000) / total) / 10 : null);
const cortar = (t, n) => (t && t.length > n ? `${t.substring(0, n - 1)}…` : t);
/**
 * Nome no eixo das barras horizontais, encurtado para caber em cerca de 1/3 da largura do gráfico (~7 px por letra, maiúscula, na
 * fonte de 11 px): o Chart.js não quebra nem encurta e cortava o começo do nome em painel estreito. O nome inteiro fica no tooltip.
 */
function rotuloQueCabe(valor) {
  return cortar(this.getLabelForValue(valor), Math.max(10, Math.floor(this.chart.width / 3 / 7)));
}
const chipDe = (mapa, valor) => {
  const s = mapa[valor] || { label: valor || "-", cor: "default" };
  return <Chip size="small" variant="outlined" color={s.cor} label={s.label} sx={{ height: 20, fontSize: "0.7rem" }} />;
};

/** Variação de contagem (%) e de taxa (pontos percentuais) contra o período anterior, para o KpiCard. */
function variacaoContagem(atual, anterior, subirEhBom = true) {
  if (anterior === null || anterior === undefined || atual === null || atual === undefined || anterior === 0) return null;
  const v = Math.round(((atual - anterior) * 1000) / anterior) / 10;
  if (v === 0) return { texto: "0%", positiva: true, subiu: true };
  return { texto: `${v > 0 ? "+" : ""}${String(v).replace(".", ",")}%`, positiva: (v > 0) === subirEhBom, subiu: v > 0 };
}
function variacaoTaxa(atual, anterior, subirEhBom = true) {
  if (anterior === null || anterior === undefined || atual === null || atual === undefined) return null;
  const v = Math.round((atual - anterior) * 10) / 10;
  if (v === 0) return { texto: "0 p.p.", positiva: true, subiu: true };
  return { texto: `${v > 0 ? "+" : ""}${String(v).replace(".", ",")} p.p.`, positiva: (v > 0) === subirEhBom, subiu: v > 0 };
}

/** Título do painel com ícone. */
const Titulo = ({ icone, texto }) => (
  <MDBox component="span" display="inline-flex" alignItems="center" gap={0.75} sx={{ color: "inherit" }}>
    <Icon sx={{ fontSize: "1.1rem !important", opacity: 0.8, color: "inherit" }}>{icone}</Icon>
    {texto}
  </MDBox>
);

const Vazio = ({ texto }) => (
  <MDTypography component="div" variant="caption" color="text" display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={4} sx={{ opacity: 0.75 }}>
    <Icon sx={{ fontSize: "2rem !important", mb: 0.5, color: "inherit" }}>insights</Icon>
    {texto}
  </MDTypography>
);

/** Valor na ponta de cada barra horizontal (opções: formatar(valor, dataset, i), cor). Só nas barras não empilhadas. */
const valorNaPonta = {
  id: "valorNaPonta",
  afterDatasetsDraw(chart, args, opcoes) {
    if (!opcoes || !opcoes.formatar) return;
    const { ctx } = chart;
    ctx.save();
    ctx.font = `600 11px ${FONTE}`;
    ctx.fillStyle = opcoes.cor;
    ctx.textBaseline = "middle";
    chart.data.datasets.forEach((ds, di) => {
      const meta = chart.getDatasetMeta(di);
      if (meta.hidden) return;
      meta.data.forEach((barra, i) => {
        const v = ds.data[i];
        if (v === null || v === undefined) return;
        const { x, y } = barra.getProps(["x", "y"], true);
        ctx.fillText(opcoes.formatar(v, di, i), x + 6, y);
      });
    });
    ctx.restore();
  },
};

/** Texto no fim da barra empilhada (opções: textos[i], cor), depois da parte visível mais comprida. */
const textoNoFim = {
  id: "textoNoFim",
  afterDatasetsDraw(chart, args, opcoes) {
    if (!opcoes || !opcoes.textos) return;
    const { ctx } = chart;
    ctx.save();
    ctx.font = `600 11px ${FONTE}`;
    ctx.fillStyle = opcoes.cor;
    ctx.textBaseline = "middle";
    opcoes.textos.forEach((texto, i) => {
      let fimX = null;
      let y = null;
      chart.data.datasets.forEach((ds, di) => {
        const meta = chart.getDatasetMeta(di);
        if (meta.hidden || !meta.data[i]) return;
        const p = meta.data[i].getProps(["x", "y"], true);
        if (fimX === null || p.x > fimX) fimX = p.x;
        y = p.y;
      });
      if (fimX !== null) ctx.fillText(texto, fimX + 6, y);
    });
    ctx.restore();
  },
};

/** % dentro de cada parte da barra empilhada de 100% (só onde cabe). */
const percentualNaParte = {
  id: "percentualNaParte",
  afterDatasetsDraw(chart, args, opcoes) {
    if (!opcoes || !opcoes.ativo) return;
    const { ctx } = chart;
    ctx.save();
    ctx.font = `700 10px ${FONTE}`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    chart.data.datasets.forEach((ds, di) => {
      const meta = chart.getDatasetMeta(di);
      if (meta.hidden) return;
      meta.data.forEach((barra, i) => {
        const v = ds.data[i];
        if (!v) return;
        const { x, y, base } = barra.getProps(["x", "y", "base"], true);
        const largura = Math.abs(x - base);
        // Com uma casa decimal quando cabe (12,5% + 87,5%, e não 13% + 88%); senão inteiro; senão nada (fica no tooltip)
        const texto = [percentual(Math.round(v * 10) / 10), percentual(Math.round(v))].find((s) => ctx.measureText(s).width + 8 <= largura);
        if (!texto) return;
        ctx.fillStyle = corTextoSobre(ds.backgroundColor);
        ctx.fillText(texto, (x + base) / 2, y);
      });
    });
    ctx.restore();
  },
};

/** Total no meio da rosca. */
const totalNoCentro = {
  id: "totalNoCentro",
  afterDraw(chart, args, opcoes) {
    if (!opcoes || opcoes.total === undefined) return;
    const { ctx, chartArea } = chart;
    const cx = (chartArea.left + chartArea.right) / 2;
    const cy = (chartArea.top + chartArea.bottom) / 2;
    ctx.save();
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillStyle = opcoes.cor;
    ctx.font = `700 20px ${FONTE}`;
    ctx.fillText(formatarNumero(opcoes.total), cx, cy - 8);
    ctx.fillStyle = opcoes.corSecundaria;
    ctx.font = `400 11px ${FONTE}`;
    ctx.fillText("e-mails enviados", cx, cy + 12);
    ctx.restore();
  },
};

/** Rosca do resultado dos e-mails (partes que não se sobrepõem) com a legenda: número e %. */
function RoscaResultado({ totais, darkMode }) {
  const modo = darkMode ? "dark" : "light";
  const tema = temaGrafico(darkMode);
  const cores = coresDoTema(darkMode);
  const partes = ORDEM.map((k) => ({ k, valor: totais[k] || 0 })).filter((p) => p.valor > 0);
  const total = partes.reduce((s, p) => s + p.valor, 0);
  if (!total) return <Vazio texto="Nenhum e-mail enviado no período." />;
  const data = {
    labels: partes.map((p) => ROTULOS[p.k]),
    datasets: [{ data: partes.map((p) => p.valor), backgroundColor: partes.map((p) => CORES[p.k][modo]), borderColor: tema.superficie, borderWidth: 2 }],
  };
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: "68%",
    plugins: {
      legend: { display: false },
      totalNoCentro: { total, cor: tema.texto, corSecundaria: tema.textoSecundario },
      tooltip: { callbacks: { label: (c) => ` ${c.label}: ${formatarNumero(c.raw)} (${percentual(parte(c.raw, total))})` } },
    },
  };
  return (
    <MDBox display="flex" flexDirection="column" gap={1.5}>
      <MDBox sx={{ height: 170 }}><Doughnut data={data} options={options} plugins={[totalNoCentro]} /></MDBox>
      <MDBox display="flex" flexDirection="column" gap={0.75}>
        {partes.map((p) => (
          <MDBox key={p.k} display="flex" alignItems="center" gap={0.75}>
            <MDBox sx={{ width: 10, height: 10, minWidth: 10, borderRadius: "50%", background: CORES[p.k][modo] }} />
            <MDTypography variant="caption" sx={{ color: cores.texto, flex: 1 }}>{ROTULOS[p.k]}</MDTypography>
            <MDTypography variant="caption" fontWeight="bold" sx={{ color: cores.texto }}>{formatarNumero(p.valor)}</MDTypography>
            <MDTypography variant="caption" sx={{ color: cores.textoSecundario, minWidth: 46, textAlign: "right" }}>{percentual(parte(p.valor, total))}</MDTypography>
          </MDBox>
        ))}
      </MDBox>
    </MDBox>
  );
}

/**
 * Lote × lote anterior (pedido da Aline em 26/10/2026): e-mails enviados, entregues (sem aviso de não entrega) e fichas que
 * responderam do lote escolhido ao lado do lote criado antes dele (no período e nos filtros da tela), com a variação na ponta.
 * Lote sem medição de entrega mostra "-" em entregues; sem o tipo de resposta configurado, sem a medida de respostas.
 */
function ComparativoLote({ lotes, darkMode, medeResposta }) {
  const modo = darkMode ? "dark" : "light";
  const tema = temaGrafico(darkMode);
  // Do mais antigo para o mais novo: o anterior de cada lote é o que vem antes nesta lista
  const ordem = useMemo(() => [...(lotes || [])].sort((a, b) => a.id - b.id), [lotes]);
  const comAnterior = ordem.slice(1).reverse();
  const [escolhido, setEscolhido] = useState(null);
  const atual = comAnterior.find((l) => l.id === escolhido) || comAnterior[0];
  if (!atual) return <Vazio texto="É preciso ao menos dois lotes no período para comparar." />;
  const anterior = ordem[ordem.findIndex((l) => l.id === atual.id) - 1];
  const rotuloLote = (l) => `Lote ${l.id}${l.criadoEm ? ` (${l.criadoEm.substring(0, 5)})` : ""}${l.teste ? " · teste" : ""}`;
  const entregues = (l) => (l.medeRetorno ? (l.entregues || 0) + (l.aguardando || 0) : null);
  const medidas = [
    { rotulo: "E-mails enviados", valor: (l) => l.emails || 0 },
    { rotulo: "Entregues", valor: entregues },
    ...(medeResposta ? [{ rotulo: "Responderam (fichas)", valor: (l) => l.respondidas || 0 }] : []),
  ];
  const valores = (l) => medidas.map((m) => m.valor(l));
  const variacao = (a, b) => {
    if (a === null || a === undefined || b === null || b === undefined) return "";
    if (!b) return a ? " · novo" : "";
    const v = Math.round(((a - b) * 1000) / b) / 10;
    return v === 0 ? " · =" : ` · ${v > 0 ? "▲" : "▼"} ${String(Math.abs(v)).replace(".", ",")}%`;
  };
  const vAnterior = valores(anterior);
  const vAtual = valores(atual);
  const data = {
    labels: medidas.map((m) => m.rotulo),
    datasets: [
      { label: rotuloLote(anterior), data: vAnterior, backgroundColor: CORES.aguardando[modo], borderRadius: 4, borderSkipped: false, maxBarThickness: 14 },
      { label: rotuloLote(atual), data: vAtual, backgroundColor: CORES.entregues[modo], borderRadius: 4, borderSkipped: false, maxBarThickness: 14 },
    ],
  };
  const options = {
    indexAxis: "y",
    responsive: true,
    maintainAspectRatio: false,
    layout: { padding: { right: 92 } },
    plugins: {
      // No lote escolhido, o número e a variação para o anterior
      valorNaPonta: { formatar: (v, di, i) => `${formatarNumero(v)}${di === 1 ? variacao(v, vAnterior[i]) : ""}`, cor: tema.texto },
      legend: { position: "bottom", labels: { color: tema.texto, boxWidth: 10, boxHeight: 10, usePointStyle: true, pointStyle: "circle", font: { size: 11 } } },
      tooltip: { callbacks: { label: (c) => ` ${c.dataset.label}: ${c.raw === null ? "sem medição" : formatarNumero(c.raw)}` } },
    },
    scales: {
      x: { beginAtZero: true, grid: { color: tema.grade }, border: { display: false }, ticks: { color: tema.textoSecundario, precision: 0, font: { size: 10 } } },
      y: { grid: { display: false }, ticks: { color: tema.texto, font: { size: 11 } } },
    },
  };
  const taxa = (l, campo) => (l[campo] === null || l[campo] === undefined ? "-" : percentual(l[campo]));
  return (
    <MDBox>
      <MDInput select fullWidth size="small" value={atual.id} onChange={(e) => setEscolhido(Number(e.target.value))} sx={{ mb: 1 }}>
        {comAnterior.map((l) => (
          <MenuItem key={l.id} value={l.id}>{`${rotuloLote(l)} · ${l.modelo || ""}`}</MenuItem>
        ))}
      </MDInput>
      <MDBox sx={{ height: medidas.length * 52 + 60 }}><Bar data={data} options={options} plugins={[valorNaPonta]} /></MDBox>
      <MDTypography variant="caption" color="text" sx={{ display: "block", mt: 1, opacity: 0.85, fontSize: "0.68rem" }}>
        {`% entrega ${taxa(atual, "taxaEntrega")} × ${taxa(anterior, "taxaEntrega")}`}
        {medeResposta ? ` · % resposta ${taxa(atual, "taxaResposta")} × ${taxa(anterior, "taxaResposta")}` : ""}
        {` (lote ${atual.id} × lote ${anterior.id}). Entregues = sem aviso de não entrega.`}
      </MDTypography>
    </MDBox>
  );
}

/** Funil em barras (largura proporcional ao número), com o ícone de cada etapa e quanto passou da anterior. */
function Funil({ funil, darkMode }) {
  const modo = darkMode ? "dark" : "light";
  const cores = coresDoTema(darkMode);
  const base = funil?.enviadas || 0;
  if (!base) return <Vazio texto="Sem lotes com medição no período." />;
  const etapas = [
    { rotulo: "Enviadas", icone: "send", valor: funil.enviadas, cor: CORES.enviadas[modo] },
    { rotulo: "Chegaram", icone: "mark_email_read", valor: funil.entregues, cor: CORES.entregues[modo], passou: "chegaram" },
    { rotulo: "Abertas (aprox.)", icone: "drafts", valor: funil.abertas, cor: CORES.aberturas[modo], passou: "abriram" },
    { rotulo: "Com clique", icone: "ads_click", valor: funil.clicadas, cor: CORES.cliques[modo], passou: "clicaram" },
    // Resposta do devedor (ocorrência registrada na ficha): pode vir sem abrir nem clicar, então a conta é sobre as que chegaram
    ...(funil.responderam !== null && funil.responderam !== undefined
      ? [{ rotulo: "Responderam", icone: "forum", valor: funil.responderam, cor: CORES.respostas[modo], passou: "das que chegaram responderam", relativoA: 1 }]
      : []),
  ];
  return (
    <MDBox>
      {etapas.map((e, i) => {
        const anterior = i > 0 ? etapas[e.relativoA ?? i - 1].valor : null;
        const semMedida = e.valor === null || e.valor === undefined;
        return (
          <MDBox key={e.rotulo}>
            {i > 0 && (
              <MDTypography variant="caption" display="flex" alignItems="center" gap={0.25} sx={{ color: cores.textoSecundario, pl: 3.5, my: 0.25, fontSize: "0.68rem" }}>
                <Icon sx={{ fontSize: "0.9rem !important" }}>south</Icon>
                {semMedida || !anterior ? "sem medição" : `${percentual(parte(e.valor, anterior))} ${e.passou}`}
              </MDTypography>
            )}
            <MDBox display="flex" alignItems="center" gap={1}>
              <MDBox display="flex" alignItems="center" justifyContent="center" sx={{ width: 26, height: 26, minWidth: 26, borderRadius: "50%", background: e.cor }}>
                <Icon sx={{ fontSize: "0.95rem !important", color: `${corTextoSobre(e.cor)} !important` }}>{e.icone}</Icon>
              </MDBox>
              <MDBox flex={1}>
                <MDBox display="flex" justifyContent="space-between">
                  <MDTypography variant="caption" sx={{ color: cores.texto }}>{e.rotulo}</MDTypography>
                  <MDTypography variant="caption" sx={{ color: cores.texto }}>
                    {semMedida ? "-" : <><b>{formatarNumero(e.valor)}</b> <span style={{ color: cores.textoSecundario }}>{percentual(parte(e.valor, base))}</span></>}
                  </MDTypography>
                </MDBox>
                <MDBox sx={{ height: 12, borderRadius: "4px", background: cores.divisoria, overflow: "hidden" }}>
                  <MDBox sx={{ width: `${semMedida || !e.valor ? 0 : Math.max(1.5, (e.valor * 100) / base)}%`, height: "100%", background: e.cor, borderRadius: "4px" }} />
                </MDBox>
              </MDBox>
            </MDBox>
          </MDBox>
        );
      })}
      <MDTypography variant="caption" color="text" sx={{ display: "block", mt: 1.25, opacity: 0.85, fontSize: "0.68rem" }}>
        Mensagens (uma por ficha) dos lotes que medem.{funil.aberturasDoLoteInteiro ? " Abertas e cliques são do lote inteiro." : ""}
      </MDTypography>
    </MDBox>
  );
}

/** E-mails enviados por dia (ou por mês, em período longo), empilhados pelo resultado. */
function GraficoEvolucao({ porDia, darkMode, porMes, medeResposta }) {
  const modo = darkMode ? "dark" : "light";
  const tema = temaGrafico(darkMode);
  const grupos = useMemo(() => {
    if (!porMes) return porDia.map((d) => ({ ...d, rotulo: moment(d.dia).format("DD/MM") }));
    const m = new Map();
    porDia.forEach((d) => {
      const chave = d.dia.substring(0, 7);
      const g = m.get(chave) || { rotulo: moment(`${chave}-01`).format("MMM/YY"), entregues: 0, naoEntregues: 0, aguardando: 0, semMedicao: 0, respondidas: 0 };
      [...ORDEM, "respondidas"].forEach((k) => { g[k] += d[k] || 0; });
      m.set(chave, g);
    });
    return [...m.values()];
  }, [porDia, porMes]);
  const series = ORDEM.filter((k) => grupos.some((d) => d[k] > 0));
  const data = {
    labels: grupos.map((d) => d.rotulo),
    datasets: series.map((k) => ({
      label: ROTULOS[k],
      data: grupos.map((d) => d[k] || 0),
      backgroundColor: CORES[k][modo],
      borderColor: tema.superficie,
      borderWidth: { top: 2 },
      borderSkipped: false,
      borderRadius: { topLeft: 3, topRight: 3 },
      maxBarThickness: 34,
    })),
  };
  // Fichas que responderam, pelo dia do envio: linha por cima das barras (pilha própria, para não somar com os e-mails)
  if (medeResposta) {
    data.datasets.push({
      type: "line",
      label: "Fichas que responderam",
      data: grupos.map((d) => d.respondidas || 0),
      stack: "respostas",
      borderColor: CORES.respostas[modo],
      backgroundColor: CORES.respostas[modo],
      borderWidth: 2,
      pointRadius: 4,
      pointHoverRadius: 6,
      pointBorderColor: tema.superficie,
      pointBorderWidth: 2,
      tension: 0,
      order: -1,
    });
  }
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: "index", intersect: false },
    plugins: {
      legend: { position: "bottom", labels: { color: tema.texto, boxWidth: 10, boxHeight: 10, usePointStyle: true, pointStyle: "circle", font: { size: 11 } } },
      tooltip: {
        callbacks: {
          label: (c) => ` ${c.dataset.label}: ${formatarNumero(c.raw)}`,
          footer: (itens) => `Total: ${formatarNumero(itens.filter((i) => i.dataset.type !== "line").reduce((s, i) => s + (i.raw || 0), 0))} e-mail(s)`,
        },
      },
    },
    scales: {
      x: { stacked: true, grid: { display: false }, ticks: { color: tema.textoSecundario, font: { size: 10 } } },
      y: { stacked: true, beginAtZero: true, grid: { color: tema.grade }, border: { display: false }, ticks: { color: tema.textoSecundario, precision: 0, font: { size: 10 } } },
    },
  };
  return <MDBox sx={{ height: 290 }}><Bar data={data} options={options} /></MDBox>;
}

/** Resultado por cliente, carteira ou modelo: barras de 100% (entregues, não entregues, aguardando), os 10 maiores. */
function GraficoDesempenho({ linhas, campo, darkMode, medeResposta }) {
  const modo = darkMode ? "dark" : "light";
  const tema = temaGrafico(darkMode);
  const itens = [...(linhas || [])].filter((l) => l.emails > 0).sort((a, b) => b.emails - a.emails).slice(0, 10);
  if (!itens.length) return <Vazio texto="Nenhum e-mail enviado no período." />;
  const series = ORDEM.filter((k) => itens.some((l) => l[k] > 0));
  const totalDe = (l) => ORDEM.reduce((s, k) => s + (l[k] || 0), 0);
  const data = {
    labels: itens.map((l) => l[campo]),
    datasets: series.map((k) => ({
      label: ROTULOS[k],
      data: itens.map((l) => (totalDe(l) ? ((l[k] || 0) * 100) / totalDe(l) : 0)),
      backgroundColor: CORES[k][modo],
      borderColor: tema.superficie,
      borderWidth: { right: 2 },
      borderSkipped: false,
      maxBarThickness: 22,
    })),
  };
  const options = {
    indexAxis: "y",
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      percentualNaParte: { ativo: true },
      legend: { position: "bottom", labels: { color: tema.texto, boxWidth: 10, boxHeight: 10, usePointStyle: true, pointStyle: "circle", font: { size: 11 } } },
      tooltip: {
        callbacks: {
          title: (c) => itens[c[0].dataIndex][campo],
          label: (c) => {
            const l = itens[c.dataIndex];
            const k = series[c.datasetIndex];
            return ` ${ROTULOS[k]}: ${formatarNumero(l[k] || 0)} de ${formatarNumero(totalDe(l))} e-mail(s) (${percentual(Math.round(c.raw * 10) / 10)})`;
          },
          afterBody: (c) => {
            if (!medeResposta || !c.length) return "";
            const l = itens[c[0].dataIndex];
            return `Responderam: ${formatarNumero(l.respondidas || 0)} de ${formatarNumero(l.enviados || 0)} ficha(s) (${percentual(l.taxaResposta)})`;
          },
        },
      },
    },
    scales: {
      x: { stacked: true, max: 100, grid: { color: tema.grade }, border: { display: false }, ticks: { color: tema.textoSecundario, callback: (v) => `${v}%`, font: { size: 10 } } },
      y: { stacked: true, grid: { display: false }, ticks: { color: tema.texto, font: { size: 11 }, callback: rotuloQueCabe } },
    },
  };
  return <MDBox sx={{ height: Math.max(180, itens.length * 34 + 70) }}><Bar data={data} options={options} plugins={[percentualNaParte]} /></MDBox>;
}

/** Barras horizontais de uma série só, com o valor na ponta. */
function GraficoBarrasH({ rotulos, valores, cor, formatar, dicas, darkMode, maximo }) {
  const tema = temaGrafico(darkMode);
  const data = { labels: rotulos, datasets: [{ data: valores, backgroundColor: cor, borderRadius: 4, borderSkipped: false, maxBarThickness: 20 }] };
  const options = {
    indexAxis: "y",
    responsive: true,
    maintainAspectRatio: false,
    layout: { padding: { right: 56 } },
    plugins: {
      legend: { display: false },
      valorNaPonta: { formatar, cor: tema.texto },
      tooltip: { callbacks: { title: (c) => (dicas ? dicas[c[0].dataIndex] : c[0].label), label: (c) => ` ${formatar(c.raw, 0, c.dataIndex)}` } },
    },
    scales: {
      x: { beginAtZero: true, max: maximo, grid: { color: tema.grade }, border: { display: false }, ticks: { color: tema.textoSecundario, precision: 0, font: { size: 10 } } },
      y: { grid: { display: false }, ticks: { color: tema.texto, font: { size: 11 }, callback: rotuloQueCabe } },
    },
  };
  return <MDBox sx={{ height: Math.max(150, rotulos.length * 30 + 40) }}><Bar data={data} options={options} plugins={[valorNaPonta]} /></MDBox>;
}

/** Abertura e clique por modelo (duas séries no mesmo eixo de %, com o número em cada barra). */
function GraficoEngajamento({ modelos, darkMode, medeResposta }) {
  const modo = darkMode ? "dark" : "light";
  const tema = temaGrafico(darkMode);
  if (!modelos.length) return <Vazio texto="Nenhum lote com medição de abertura, clique ou resposta." />;
  const data = {
    labels: modelos.map((m) => m.modelo),
    datasets: [
      { label: "% abertura", data: modelos.map((m) => m.taxaAbertura ?? null), backgroundColor: CORES.aberturas[modo], borderRadius: 4, borderSkipped: false, maxBarThickness: 14 },
      { label: "% clique", data: modelos.map((m) => m.taxaClique ?? null), backgroundColor: CORES.cliques[modo], borderRadius: 4, borderSkipped: false, maxBarThickness: 14 },
      // % das fichas enviadas do modelo com resposta do devedor registrada na ficha
      ...(medeResposta ? [{ label: "% resposta", data: modelos.map((m) => m.taxaResposta ?? null), backgroundColor: CORES.respostas[modo], borderRadius: 4, borderSkipped: false, maxBarThickness: 14 }] : []),
    ],
  };
  const options = {
    indexAxis: "y",
    responsive: true,
    maintainAspectRatio: false,
    layout: { padding: { right: 48 } },
    plugins: {
      valorNaPonta: { formatar: (v) => percentual(v), cor: tema.texto },
      legend: { position: "bottom", labels: { color: tema.texto, boxWidth: 10, boxHeight: 10, usePointStyle: true, pointStyle: "circle", font: { size: 11 } } },
      tooltip: { callbacks: { title: (c) => modelos[c[0].dataIndex].modelo, label: (c) => ` ${c.dataset.label}: ${percentual(c.raw)}` } },
    },
    scales: {
      x: { beginAtZero: true, grid: { color: tema.grade }, border: { display: false }, ticks: { color: tema.textoSecundario, callback: (v) => `${v}%`, font: { size: 10 } } },
      y: { grid: { display: false }, ticks: { color: tema.texto, font: { size: 11 }, callback: rotuloQueCabe } },
    },
  };
  return <MDBox sx={{ height: Math.max(170, modelos.length * (medeResposta ? 54 : 40) + 60) }}><Bar data={data} options={options} plugins={[valorNaPonta]} /></MDBox>;
}

/** Domínios: e-mails sem falha e não entregues (empilhados), com o total e o % que voltou na ponta. */
function GraficoDominios({ dominios, darkMode }) {
  const modo = darkMode ? "dark" : "light";
  const tema = temaGrafico(darkMode);
  if (!dominios.length) return <Vazio texto="Sem e-mails medidos no período." />;
  const itens = dominios.slice(0, 8);
  const data = {
    labels: itens.map((d) => d.dominio),
    datasets: [
      { label: "Sem falha", data: itens.map((d) => d.emails - d.naoEntregues), backgroundColor: CORES.entregues[modo] },
      { label: "Não entregues", data: itens.map((d) => d.naoEntregues), backgroundColor: CORES.naoEntregues[modo] },
    ]
      .filter((ds) => ds.data.some((v) => v > 0))
      .map((ds) => ({ ...ds, borderColor: tema.superficie, borderWidth: { right: 2 }, borderSkipped: false, maxBarThickness: 18 })),
  };
  const options = {
    indexAxis: "y",
    responsive: true,
    maintainAspectRatio: false,
    layout: { padding: { right: 86 } },
    plugins: {
      textoNoFim: { cor: tema.texto, textos: itens.map((d) => `${formatarNumero(d.emails)} · ${percentual(d.taxaNaoEntrega)}`) },
      legend: { position: "bottom", labels: { color: tema.texto, boxWidth: 10, boxHeight: 10, usePointStyle: true, pointStyle: "circle", font: { size: 11 } } },
      tooltip: { callbacks: { title: (c) => itens[c[0].dataIndex].dominio, label: (c) => ` ${c.dataset.label}: ${formatarNumero(c.raw)}` } },
    },
    scales: {
      x: { stacked: true, beginAtZero: true, grid: { color: tema.grade }, border: { display: false }, ticks: { color: tema.textoSecundario, precision: 0, font: { size: 10 } } },
      y: { stacked: true, grid: { display: false }, ticks: { color: tema.texto, font: { size: 11 }, callback: rotuloQueCabe } },
    },
  };
  return (
    <>
      <MDBox sx={{ height: Math.max(170, itens.length * 30 + 70) }}><Bar data={data} options={options} plugins={[textoNoFim]} /></MDBox>
      <MDTypography variant="caption" color="text" sx={{ display: "block", mt: 1, opacity: 0.85, fontSize: "0.68rem" }}>
        Na ponta: e-mails enviados ao domínio · % que não foi entregue.
      </MDTypography>
    </>
  );
}

/** Barra de % dentro da célula da tabela (trilho + preenchimento) com o número ao lado. */
function BarraCelula({ valor, cor, cores }) {
  if (valor === null || valor === undefined) return <span>-</span>;
  return (
    // color: inherit = a cor do texto da célula (o MDBox usa cor escura por padrão e o % sumia no tema escuro)
    <MDBox display="flex" alignItems="center" gap={1} justifyContent="flex-end" sx={{ color: "inherit" }}>
      <MDBox sx={{ width: 64, height: 8, borderRadius: 4, background: cores.divisoria, overflow: "hidden" }}>
        <MDBox sx={{ width: `${Math.min(100, valor)}%`, height: "100%", background: cor, borderRadius: 4 }} />
      </MDBox>
      <span style={{ minWidth: 44, textAlign: "right" }}>{percentual(valor)}</span>
    </MDBox>
  );
}

/** Tabela simples (cabeçalho no tom do painel, números à direita). */
function Tabela({ colunas, linhas, cores, vazio, chave, altura = 380 }) {
  const cab = { fontWeight: "bold", fontSize: "0.72rem", backgroundColor: cores.painel, color: cores.texto, whiteSpace: "nowrap", py: 1 };
  const cel = { fontSize: "0.78rem", color: cores.texto, py: 0.75, verticalAlign: "middle" };
  return (
    <TableContainer sx={{ boxShadow: "none", border: `1px solid ${cores.divisoria}`, borderRadius: 1, maxHeight: altura }}>
      <Table size="small" stickyHeader>
        <TableHead sx={{ display: "table-header-group" }}>
          <TableRow>
            {colunas.map((c) => (
              <TableCell key={c.rotulo} align={c.numero ? "right" : "left"} sx={cab}>
                {c.dica ? <Tooltip title={c.dica} placement="top"><span>{c.rotulo}</span></Tooltip> : c.rotulo}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {linhas.length === 0 && (
            <TableRow><TableCell colSpan={colunas.length} align="center" sx={{ ...cel, py: 2 }}>{vazio}</TableCell></TableRow>
          )}
          {linhas.map((l, i) => (
            <TableRow key={l[chave] ?? i} hover>
              {colunas.map((c) => (
                <TableCell key={c.rotulo} align={c.numero ? "right" : "left"} sx={{ ...cel, whiteSpace: c.numero ? "nowrap" : "normal" }}>
                  {c.valor(l, i)}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

/** Detalhe do lote: cada envio com a entrega e o motivo (com a permissão da análise). */
function DetalheLote({ lote, aoFechar, cores }) {
  const [dados, setDados] = useState(null);
  const [situacao, setSituacao] = useState(null);
  useEffect(() => {
    if (!lote) {
      setDados(null);
      setSituacao(null);
      return;
    }
    get(`/mala-direta/analise/lote/${lote}${situacao ? `?situacao=${situacao.value}` : ""}`)
      .then(setDados)
      .catch((e) => errorAlert(e?.message || "Erro ao abrir o lote."));
  }, [lote, situacao]);
  const colunas = [
    { rotulo: "Situação", valor: (e) => SITUACAO_ENVIO[e.situacao] || e.situacao },
    { rotulo: "Entrega", valor: (e) => (e.entrega ? chipDe(ENTREGA, e.entrega) : "-") },
    { rotulo: "Ficha", valor: (e) => (e.ficha ? `#${e.ficha}` : "-") },
    { rotulo: "Devedor", valor: (e) => e.nome || "-" },
    { rotulo: "Cliente · Carteira", valor: (e) => `${e.cliente || "-"}${e.carteira ? ` · ${e.carteira}` : ""}` },
    { rotulo: "E-mails", valor: (e) => e.emails || "-" },
    { rotulo: "Enviado em", valor: (e) => e.enviadoEm || "-" },
    {
      rotulo: "Erro / não entregue",
      valor: (e) => (
        <>
          {e.erro}
          {(e.retornos || []).map((x) => <div key={x.email}>{x.email}: {x.motivo}{x.marcouRuim ? " · marcado como ruim" : ""}</div>)}
          {!e.erro && !(e.retornos || []).length && "-"}
        </>
      ),
    },
  ];
  return (
    <Dialog open={!!lote} onClose={aoFechar} maxWidth="lg" fullWidth PaperProps={{ sx: { background: cores.cartao } }}>
      <DialogTitle sx={{ color: cores.texto, pb: 1 }}>
        <MDBox display="flex" justifyContent="space-between" alignItems="center" sx={{ color: "inherit" }}>
          <span>Lote nº {lote}{dados ? ` · ${dados.modelo || ""}` : ""}</span>
          <IconButton size="small" onClick={aoFechar}><Icon sx={{ color: cores.texto }}>close</Icon></IconButton>
        </MDBox>
      </DialogTitle>
      <DialogContent>
        {!dados ? <MDBox display="flex" justifyContent="center" py={4}><CircularProgress size={28} color="info" /></MDBox> : (
          <>
            <MDBox display="flex" justifyContent="space-between" alignItems="flex-end" gap={2} flexWrap="wrap" mb={1}>
              <MDTypography variant="caption" sx={{ color: cores.textoSecundario }}>
                Criado em {dados.criadoEm} por {dados.usuario || "-"} · Sai por: {dados.remetente} · Filtros: {dados.filtros || "-"}
              </MDTypography>
              <MDBox sx={{ width: 240 }}>
                <MDSelect name="situacaoEnvio" isClearable placeholder="Todas as situações" value={situacao} onChange={setSituacao}
                  options={[...Object.entries(SITUACAO_ENVIO).filter(([k]) => k !== "ENVIANDO").map(([value, label]) => ({ value, label })),
                    ...(dados.medeRetorno ? [{ value: "NAO_ENTREGUE", label: "Não entregue (voltou)" }] : [])]} />
              </MDBox>
            </MDBox>
            {(dados.errosRetorno || []).map((e) => <MDTypography key={e} variant="caption" display="block" color="warning" mb={0.5}>{e}</MDTypography>)}
            <Tabela colunas={colunas} linhas={dados.envios || []} cores={cores} chave="id" vazio="Nenhum envio nesta situação." altura="55vh" />
            <MDTypography variant="caption" display="block" mt={1} sx={{ color: cores.textoSecundario }}>
              {dados.enviosTotal > (dados.envios || []).length
                ? `Mostrando ${formatarNumero(dados.envios.length)} de ${formatarNumero(dados.enviosTotal)}: a planilha do lote (Envio Mala Direta) traz todos.`
                : `${formatarNumero(dados.enviosTotal)} envio(s).`}
            </MDTypography>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

/**
 * Análise da Mala Direta (c084): os lotes criados no período (com a visibilidade do usuário), quantos e-mails foram
 * entregues, quantos voltaram e por quê, os erros no envio, as aberturas e os cliques (aproximados, por lote, sem
 * identificar o devedor), com a comparação contra o período anterior. Backend: GET /mala-direta/analise.
 */
export default function MalaDiretaAnalise() {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const cores = useMemo(() => coresDoTema(darkMode), [darkMode]);
  const modo = darkMode ? "dark" : "light";

  const [inicio, setInicio] = useState(moment().subtract(29, "days").format("YYYY-MM-DD"));
  const [fim, setFim] = useState(moment().format("YYYY-MM-DD"));
  const [carteiras, setCarteiras] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [modelos, setModelos] = useState([]);
  const [situacoes, setSituacoes] = useState([]);
  // Lotes de teste (chave "Envio de teste" do Novo envio): fora da conta por padrão, para não misturar com os envios reais
  const [incluirTeste, setIncluirTeste] = useState(false);
  const [dados, setDados] = useState(null);
  const [anterior, setAnterior] = useState(null);
  const [carregando, setCarregando] = useState(false);
  const [exportando, setExportando] = useState(false);
  const [erro, setErro] = useState("");
  const [busca, setBusca] = useState("");
  const [loteAberto, setLoteAberto] = useState(null);
  const [dimensao, setDimensao] = useState(DIMENSOES[0]);
  // Painel Resultado dos e-mails: o período inteiro (rosca) ou um lote ao lado do anterior
  const [visaoResultado, setVisaoResultado] = useState("periodo");

  const consulta = useCallback((de, ate) => {
    const q = new URLSearchParams({ inicio: de, fim: ate });
    carteiras.forEach((c) => q.append("carteiras", c.value));
    clientes.forEach((c) => q.append("clientes", c.value));
    modelos.forEach((m) => q.append("modelos", m.value));
    situacoes.forEach((s) => q.append("situacoes", s.value));
    if (incluirTeste) q.append("teste", "true");
    return q;
  }, [carteiras, clientes, modelos, situacoes, incluirTeste]);

  const carregar = useCallback(() => {
    if (!inicio || !fim) {
      setErro("Escolha o período.");
      return;
    }
    // Período anterior do mesmo tamanho, logo antes (só os totais, para a variação dos cards)
    const dias = moment(fim).diff(moment(inicio), "days") + 1;
    const qAnt = consulta(moment(inicio).subtract(dias, "days").format("YYYY-MM-DD"), moment(inicio).subtract(1, "days").format("YYYY-MM-DD"));
    qAnt.append("totais", "true");
    setCarregando(true);
    setErro("");
    Promise.all([get(`/mala-direta/analise?${consulta(inicio, fim).toString()}`), get(`/mala-direta/analise?${qAnt.toString()}`).catch(() => null)])
      .then(([atual, ant]) => {
        setDados(atual);
        setAnterior(ant?.totais || null);
      })
      .catch((e) => setErro(e?.message || "Erro ao carregar a análise."))
      .finally(() => setCarregando(false));
  }, [inicio, fim, consulta]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const exportar = () => {
    setExportando(true);
    getFile(`/mala-direta/analise/exportar?${consulta(inicio, fim).toString()}`, `ANALISE_MALA_DIRETA_${inicio}_${fim}.xlsx`)
      .catch((e) => errorAlert(e?.message || "Erro ao gerar a planilha."))
      .finally(() => setExportando(false));
  };

  const t = dados?.totais;
  const a = anterior;
  const opcoes = dados?.opcoes || { carteiras: [], clientes: [], modelos: [] };
  const semDados = dados && t && t.fichas === 0;
  const porMes = moment(fim).diff(moment(inicio), "days") > 62;
  const rotuloVariacao = "vs. período anterior";
  const cor = (k) => CORES[k][modo];

  const lotesFiltrados = useMemo(() => {
    const b = busca.trim().toLowerCase();
    const lista = dados?.porLote || [];
    if (!b) return lista;
    return lista.filter((l) => [`${l.id}`, l.modelo, l.por, l.criadoEm].some((v) => (v || "").toString().toLowerCase().includes(b)));
  }, [dados, busca]);

  const medeResposta = !!dados?.totais?.medeResposta;
  const engajamento = useMemo(() => (dados?.porModelo || [])
    .filter((m) => m.medeAbertura || m.medeClique || (medeResposta && m.enviados > 0))
    .sort((x, y) => (y.taxaAbertura ?? -1) - (x.taxaAbertura ?? -1))
    .slice(0, 8), [dados, medeResposta]);

  const colunasLote = [
    { rotulo: "Nº", valor: (l) => l.id },
    { rotulo: "Criado em", valor: (l) => l.criadoEm },
    { rotulo: "Modelo", valor: (l) => l.modelo },
    { rotulo: "Por", valor: (l) => l.por || "-" },
    {
      rotulo: "Situação",
      valor: (l) => (
        <MDBox display="flex" gap={0.5} flexWrap="wrap">
          {chipDe(SITUACAO_LOTE, l.situacao)}
          {l.teste && (
            <Tooltip title="Envio de teste: só para o e-mail de teste, sem ocorrência na ficha">
              <Chip size="small" variant="outlined" color="warning" label="Teste" sx={{ height: 20, fontSize: "0.7rem" }} />
            </Tooltip>
          )}
        </MDBox>
      ),
    },
    { rotulo: "Fichas", numero: true, valor: (l) => formatarNumero(l.enviados) },
    { rotulo: "E-mails", numero: true, valor: (l) => formatarNumero(l.emails) },
    { rotulo: "Não entregues", numero: true, valor: (l) => (l.medeRetorno ? formatarNumero(l.naoEntregues) : "-") },
    { rotulo: "% entrega", numero: true, dica: "E-mails sem aviso de não entrega ÷ e-mails medidos; os enviados há menos de 48 h contam como entregues até voltar um aviso", valor: (l) => (l.medeRetorno ? <BarraCelula valor={l.taxaEntrega} cor={cor("entregues")} cores={cores} /> : "-") },
    { rotulo: "% abertura", numero: true, dica: "Aproximado, por lote (sem identificar o devedor)", valor: (l) => (l.medeAbertura ? <BarraCelula valor={l.taxaAbertura} cor={cor("aberturas")} cores={cores} /> : "-") },
    { rotulo: "% clique", numero: true, dica: "Mensagens com clique em algum link do modelo, por lote", valor: (l) => (l.medeClique ? <BarraCelula valor={l.taxaClique} cor={cor("cliques")} cores={cores} /> : "-") },
    ...(t?.medeResposta ? [{ rotulo: "% resposta", numero: true, dica: "Fichas enviadas do lote com ocorrência de resposta do devedor", valor: (l) => <BarraCelula valor={l.taxaResposta} cor={cor("respostas")} cores={cores} /> }] : []),
    { rotulo: "Erros", numero: true, valor: (l) => formatarNumero(l.erros) },
    {
      rotulo: "",
      valor: (l) => (
        <Tooltip title="Ver os envios do lote"><IconButton size="small" color="info" onClick={() => setLoteAberto(l.id)}><Icon fontSize="small">visibility</Icon></IconButton></Tooltip>
      ),
    },
  ];

  return (
    <Grid container spacing={3} pt={1} pb={3}>
      <Grid item xs={12}>
        <Card sx={{ px: 2, py: 1.5, background: cores.cartao }}>
          <Grid container spacing={2} alignItems="flex-start">
            <Grid item xs={12} md={6} xl={3}>
              <MDPeriodo inicio={inicio} fim={fim} onChange={({ inicio: i, fim: f }) => { setInicio(i); setFim(f); }} />
            </Grid>
            <Grid item xs={12} sm={6} md={3} xl={2}>
              <InputLabel sx={rotuloFiltro}>Cliente</InputLabel>
              <MDSelect isMulti name="clientes" placeholder="Todos" value={clientes} options={opcoes.clientes} onChange={(v) => setClientes(v || [])} />
            </Grid>
            <Grid item xs={12} sm={6} md={3} xl={2}>
              <InputLabel sx={rotuloFiltro}>Carteira</InputLabel>
              <MDSelect isMulti name="carteiras" placeholder="Todas" value={carteiras} options={opcoes.carteiras} onChange={(v) => setCarteiras(v || [])} />
            </Grid>
            <Grid item xs={12} sm={6} md={4} xl={2}>
              <InputLabel sx={rotuloFiltro}>Modelo</InputLabel>
              <MDSelect isMulti name="modelos" placeholder="Todos" value={modelos} options={opcoes.modelos} onChange={(v) => setModelos(v || [])} />
            </Grid>
            <Grid item xs={12} sm={6} md={4} xl={1.5}>
              <InputLabel sx={rotuloFiltro}>Situação</InputLabel>
              <MDSelect isMulti name="situacoes" placeholder="Todas" value={situacoes} options={OPCOES_SITUACAO} onChange={(v) => setSituacoes(v || [])} />
            </Grid>
            <Grid item xs={12} sm={6} md={4} xl={1.5}>
              <InputLabel sx={rotuloFiltro}>&nbsp;</InputLabel>
              <MDButton variant="contained" color="info" size="medium" fullWidth onClick={carregar} disabled={carregando} sx={{ height: 38, px: 1, whiteSpace: "nowrap" }}>
                {carregando ? <CircularProgress size={14} color="inherit" sx={{ mr: 0.5 }} /> : <Icon sx={{ mr: 0.5 }}>refresh</Icon>}
                Atualizar
              </MDButton>
            </Grid>
            <Grid item xs={12} sx={{ pt: "8px !important" }}>
              <MDBox display="flex" alignItems="flex-start" justifyContent="space-between" gap={2} flexWrap={{ xs: "wrap", md: "nowrap" }}>
                <MDBox>
                  {erro && <MDTypography variant="caption" color="error" fontWeight="bold" sx={{ px: 1, display: "block" }}>{erro}</MDTypography>}
                  <MDTypography variant="caption" color="text" sx={{ px: 1, display: "block" }}>
                    Lotes criados no período (até 12 meses), só as fichas das carteiras e clientes que você vê. E-mails contam destinatários (uma
                    ficha pode ter mais de um); entregue = sem aviso de não entrega (o aviso pode voltar em até 48 h). Aberturas e cliques são aproximados, contados por lote
                    sem identificar o devedor. As variações comparam com o período anterior do mesmo tamanho.
                    {dados?.geradoEm ? ` Dados de ${dados.geradoEm}.` : ""}
                  </MDTypography>
                  {!incluirTeste && dados?.lotesTesteFora > 0 && (
                    <MDTypography variant="caption" color="warning" fontWeight="medium" sx={{ px: 1, display: "block" }}>
                      {dados.lotesTesteFora} envio(s) de teste no período fora da conta: ligue "Incluir envios de teste" para ver.
                    </MDTypography>
                  )}
                </MDBox>
                <FormControlLabel sx={{ mr: 0, flexShrink: 0 }}
                  control={<Switch checked={incluirTeste} onChange={(e) => setIncluirTeste(e.target.checked)} />}
                  label={<MDTypography variant="caption" color="text" sx={{ whiteSpace: "nowrap" }}>Incluir envios de teste</MDTypography>} />
                <MDButton variant="outlined" color="info" size="small" onClick={exportar} disabled={exportando || !dados || semDados} sx={{ whiteSpace: "nowrap", flexShrink: 0 }}>
                  {exportando ? <CircularProgress size={14} color="inherit" sx={{ mr: 0.5 }} /> : <Icon sx={{ mr: 0.5 }}>file_download</Icon>}
                  Exportar planilha
                </MDButton>
              </MDBox>
            </Grid>
          </Grid>
        </Card>
      </Grid>

      {carregando && dados && (
        <Grid item xs={12} sx={{ pt: "12px !important" }}><LinearProgress color="info" sx={{ borderRadius: 1 }} /></Grid>
      )}
      {carregando && !dados && (
        <Grid item xs={12}><MDBox display="flex" justifyContent="center" py={8}><CircularProgress size={36} color="info" /></MDBox></Grid>
      )}

      {semDados && (
        <Grid item xs={12}>
          <Painel><Vazio texto="Nenhum lote de Mala Direta criado no período (ou nos clientes, carteiras, modelos e situações escolhidos)." /></Painel>
        </Grid>
      )}

      {dados && t && !semDados && (
        <Grid item xs={12} sx={{ opacity: carregando ? 0.5 : 1, transition: "opacity 0.2s" }}>
          <Grid container spacing={3}>
            {/* Entrega numa linha (4) e engajamento na outra (3); a partir de 1800 px, as 7 numa linha só */}
            <Grid item xs={12} sm={6} lg={3} sx={SETE_POR_LINHA}>
              <KpiCard compacto icone="send" cor={cor("enviadas")} rotulo="E-mails Enviados" valor={formatarNumero(t.emails)}
                detalhe={`${formatarNumero(t.enviados)} ficha(s) em ${formatarNumero(t.lotes)} lote(s)`} detalheFixo
                variacao={variacaoContagem(t.emails, a?.emails)} rotuloVariacao={rotuloVariacao} />
            </Grid>
            <Grid item xs={12} sm={6} lg={3} sx={SETE_POR_LINHA}>
              {/* Entregue = sem aviso de não entrega; os enviados há menos de 48 h contam, mas o aviso ainda pode voltar */}
              <KpiCard compacto icone="mark_email_read" cor={cor("entregues")} rotulo="Entregues" valor={formatarNumero((t.entregues || 0) + (t.aguardando || 0))}
                detalhe={t.taxaEntrega != null ? `${percentual(t.taxaEntrega)} de entrega${t.aguardando ? ` · ${formatarNumero(t.aguardando)} em até 48 h` : ""}` : "sem resultado ainda"} detalheFixo
                variacao={variacaoTaxa(t.taxaEntrega, a?.taxaEntrega)} rotuloVariacao={rotuloVariacao} />
            </Grid>
            <Grid item xs={12} sm={6} lg={3} sx={SETE_POR_LINHA}>
              <KpiCard compacto icone="unsubscribe" cor={cor("naoEntregues")} rotulo="Não Entregues" valor={formatarNumero(t.naoEntregues)}
                detalhe={`${percentual(t.taxaNaoEntrega)} · ${formatarNumero(t.marcadosRuins)} marcado(s) ruim`} detalheFixo
                variacao={variacaoTaxa(t.taxaNaoEntrega, a?.taxaNaoEntrega, false)} rotuloVariacao={rotuloVariacao} />
            </Grid>
            <Grid item xs={12} sm={6} lg={3} sx={SETE_POR_LINHA}>
              <KpiCard compacto icone="error_outline" cor={cor("naoEntregues")} rotulo="Erros no Envio" valor={formatarNumero(t.erros)}
                detalhe={`${formatarNumero(t.semEmail)} ficha(s) sem e-mail${t.naFila ? ` · ${formatarNumero(t.naFila)} na fila` : ""}`} detalheFixo
                variacao={variacaoContagem(t.erros, a?.erros, false)} rotuloVariacao={rotuloVariacao} />
            </Grid>
            <Grid item xs={12} sm={6} lg={4} sx={SETE_POR_LINHA}>
              <KpiCard compacto icone="drafts" cor={cor("aberturas")} rotulo="Abertos (aprox.)" valor={t.medeAbertura ? formatarNumero(t.aberturas) : "-"}
                detalhe={t.medeAbertura ? `${percentual(t.taxaAbertura)} das mensagens` : "lotes sem medição de abertura"} detalheFixo
                variacao={t.medeAbertura ? variacaoTaxa(t.taxaAbertura, a?.taxaAbertura) : null} rotuloVariacao={rotuloVariacao} />
            </Grid>
            <Grid item xs={12} sm={6} lg={4} sx={SETE_POR_LINHA}>
              <KpiCard compacto icone="ads_click" cor={cor("cliques")} rotulo="Cliques" valor={t.medeClique ? formatarNumero(t.cliques) : "-"}
                detalhe={t.medeClique ? `${percentual(t.taxaClique)} das mensagens` : "lotes sem link medido"} detalheFixo
                variacao={t.medeClique ? variacaoTaxa(t.taxaClique, a?.taxaClique) : null} rotuloVariacao={rotuloVariacao} />
            </Grid>
            <Grid item xs={12} sm={6} lg={4} sx={SETE_POR_LINHA}>
              <Tooltip title={t.medeResposta
                ? "Fichas enviadas com ocorrência de resposta do devedor, do tipo de Parametrização do Sistema > Resposta do devedor à Mala Direta. A resposta conta para o último e-mail enviado antes dela (o da mesma ficha ou, sem ele, o do devedor), até um novo envio."
                : "Escolha o tipo de ocorrência em Parametrização do Sistema > Resposta do devedor à Mala Direta: o operador registra esse tipo na ficha quando o devedor responde."}>
                <MDBox sx={{ height: "100%" }}>
                  <KpiCard compacto icone="forum" cor={cor("respostas")} rotulo="Respostas" valor={t.medeResposta ? formatarNumero(t.respondidas) : "-"}
                    detalhe={t.medeResposta ? `${percentual(t.taxaResposta)} das fichas enviadas` : "tipo de ocorrência não configurado"} detalheFixo
                    variacao={t.medeResposta ? variacaoTaxa(t.taxaResposta, a?.taxaResposta) : null} rotuloVariacao={rotuloVariacao} />
                </MDBox>
              </Tooltip>
            </Grid>

            <Grid item xs={12} xl={6}>
              <Painel rotulo={<Titulo icone="bar_chart" texto={`Evolução dos envios (por ${porMes ? "mês" : "dia"})`} />}>
                {dados.porDia.length > 0
                  ? <GraficoEvolucao porDia={dados.porDia} darkMode={darkMode} porMes={porMes} medeResposta={medeResposta} />
                  : <Vazio texto="Nenhum e-mail enviado no período." />}
              </Painel>
            </Grid>
            <Grid item xs={12} md={6} xl={3}>
              <Painel
                rotulo={<Titulo icone="donut_large" texto="Resultado dos e-mails" />}
                acao={(
                  <MDBox display="flex" gap={0.5}>
                    {[["periodo", "Período"], ["lote", "Lote × anterior"]].map(([v, r]) => (
                      <MDButton key={v} size="small" color="info" variant={visaoResultado === v ? "contained" : "outlined"}
                        onClick={() => setVisaoResultado(v)} sx={{ minHeight: 28, height: 28, px: 1 }}>
                        {r}
                      </MDButton>
                    ))}
                  </MDBox>
                )}
              >
                {visaoResultado === "lote"
                  ? <ComparativoLote lotes={dados.porLote} darkMode={darkMode} medeResposta={medeResposta} />
                  : <RoscaResultado totais={t} darkMode={darkMode} />}
              </Painel>
            </Grid>
            <Grid item xs={12} md={6} xl={3}>
              <Painel rotulo={<Titulo icone="filter_alt" texto="Funil das mensagens" />}>
                <Funil funil={dados.funil} darkMode={darkMode} />
              </Painel>
            </Grid>

            <Grid item xs={12} xl={7}>
              <Painel
                rotulo={<Titulo icone="stacked_bar_chart" texto={`Entrega por ${dimensao.rotulo.toLowerCase()}`} />}
                acao={(
                  <MDBox display="flex" gap={0.5}>
                    {DIMENSOES.map((d) => (
                      <MDButton key={d.chave} size="small" color="info" variant={d.chave === dimensao.chave ? "contained" : "outlined"}
                        onClick={() => setDimensao(d)} sx={{ minHeight: 28, height: 28, px: 1.25 }}>
                        {d.rotulo}
                      </MDButton>
                    ))}
                  </MDBox>
                )}
              >
                <GraficoDesempenho linhas={dados[dimensao.chave]} campo={dimensao.campo} darkMode={darkMode} medeResposta={medeResposta} />
                <MDTypography variant="caption" color="text" sx={{ display: "block", mt: 1, opacity: 0.85, fontSize: "0.68rem" }}>
                  Os 10 com mais e-mails. Cada barra é 100% dos e-mails enviados por ele.
                </MDTypography>
              </Painel>
            </Grid>
            <Grid item xs={12} xl={5}>
              <Painel rotulo={<Titulo icone="report_problem" texto="Motivos de falha" />}>
                {dados.motivos.length > 0 ? (
                  <GraficoBarrasH
                    darkMode={darkMode}
                    cor={cor("naoEntregues")}
                    rotulos={dados.motivos.map((m) => m.motivo)}
                    dicas={dados.motivos.map((m) => `${m.motivo} (${m.tipo.toLowerCase()})${m.marcadosRuins ? ` · ${m.marcadosRuins} marcado(s) como ruim` : ""}`)}
                    valores={dados.motivos.map((m) => m.quantidade)}
                    formatar={(v, d, i) => `${formatarNumero(v)} · ${percentual(dados.motivos[i].percentual)}`}
                  />
                ) : <Vazio texto="Nenhuma falha no período." />}
                {dados.motivos.length > 0 && (
                  <MDTypography variant="caption" color="text" sx={{ display: "block", mt: 1, opacity: 0.85, fontSize: "0.68rem" }}>
                    Não entregues (voltaram) e erros no envio. Na ponta: quantidade · % do total de falhas. Passe o mouse para ver o tipo.
                  </MDTypography>
                )}
              </Painel>
            </Grid>

            <Grid item xs={12} md={6} xl={4}>
              <Painel rotulo={<Titulo icone="campaign" texto={medeResposta ? "Abertura, clique e resposta por modelo" : "Abertura e clique por modelo"} />}>
                <GraficoEngajamento modelos={engajamento} darkMode={darkMode} medeResposta={medeResposta} />
              </Painel>
            </Grid>
            <Grid item xs={12} md={6} xl={4}>
              <Painel rotulo={<Titulo icone="alternate_email" texto="Domínios mais usados" />}>
                <GraficoDominios dominios={dados.dominios} darkMode={darkMode} />
              </Painel>
            </Grid>
            <Grid item xs={12} xl={4}>
              <Painel rotulo={<Titulo icone="link" texto="Links mais clicados" />}>
                {dados.links.length > 0 ? (
                  <GraficoBarrasH
                    darkMode={darkMode}
                    cor={cor("cliques")}
                    rotulos={dados.links.map((l) => l.link.replace(/^https?:\/\/(www\.)?/, ""))}
                    dicas={dados.links.map((l) => l.link)}
                    valores={dados.links.map((l) => l.cliques)}
                    formatar={(v) => formatarNumero(v)}
                  />
                ) : <Vazio texto="Nenhum clique medido no período." />}
              </Painel>
            </Grid>

            <Grid item xs={12}>
              <Painel
                rotulo={<Titulo icone="inventory_2" texto="Lotes" />}
                acao={(
                  <MDBox sx={{ width: 280 }}>
                    <MDInput size="small" fullWidth placeholder="Buscar lote, modelo ou quem enviou..." value={busca} onChange={(e) => setBusca(e.target.value)} />
                  </MDBox>
                )}
              >
                <Tabela colunas={colunasLote} linhas={lotesFiltrados} cores={cores} chave="id" vazio="Nenhum lote." />
              </Painel>
            </Grid>
          </Grid>
        </Grid>
      )}

      <DetalheLote lote={loteAberto} aoFechar={() => setLoteAberto(null)} cores={cores} />
    </Grid>
  );
}
